import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Checkbox, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSION_KEYS, PERMISSION_LABELS } from '../constants/permissions';

interface User {
    id: string;
    email: string;
    name?: string;
    admin?: boolean;
    editMapStationsNotes?: boolean;
    createEditEventInfo?: boolean;
    viewWasteData?: boolean;
    editWasteData?: boolean;
    generateReports?: boolean;
    editTodos?: boolean;
    completeTodos?: boolean;
    viewInventory?: boolean;
    manageInventory?: boolean;
    editUsers?: boolean;
}

interface Props {
    open: boolean;
    onClose: () => void;
    user: User | null;
}

export default function UserPermissionsModal(props: Props) {
    const { open, onClose, user } = props;
    const navigate = useNavigate();
    const { canEditUsers } = usePermissions();

    if (!user) {
        return null;
    }

    const handleEditUser = () => {
        onClose();
        if (user) navigate(`/user/${user.id}`);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={600}>
                        User Permissions
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* User Info */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        User
                    </Typography>
                    <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                            {user.name || user.email}
                        </Typography>
                        {user.name && (
                            <Typography variant="caption" color="textSecondary">
                                {user.email}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Permissions */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Permissions
                    </Typography>
                    <Box
                        sx={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: '4px',
                            p: 1,
                        }}
                    >
                        <Stack spacing={1}>
                            {PERMISSION_KEYS.map(key => (
                                <Box
                                    key={key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        py: 0.5,
                                    }}
                                >
                                    <Checkbox checked={(user[key as keyof User] as boolean) || false} disabled size="small" />
                                    <Typography variant="body2">{PERMISSION_LABELS[key]}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                {canEditUsers && (
                    <Button onClick={handleEditUser} variant="contained">
                        Edit User
                    </Button>
                )}
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
